import { useMemo, useState } from 'react';
import { ChevronDown, Plus } from 'lucide-react';

import { useGameSelection } from '@/hooks/useGameSelection';
import { useAppSelector } from '@/store/hooks';
import { useProfilesQuery } from '@/services/endpoints/games';
import { useGetLinkQuery } from '@/services/qchessApi';
import { useGetCocLinkQuery } from '@/services/qclansApi';
import { useMeQuery, useProfileMeQuery } from '@/services/qbotApi';
import { GamePicker } from './GamePicker';
import { CHESS_GAME_ID, COC_GAME_ID, featuresForGame } from '@/lib/gameFeatures';
import { cn } from '@/lib/cn';
import type { Profile } from '@/types';

interface GameHeaderProps {
  className?: string;
}

/**
 * Game selector — shows the active game alongside the user's QLASH nickname
 * and the game-specific gamertag (Clash Royale player tag, Riot
 * `gameName#tagLine`, etc.). Tap opens `GamePicker` to switch.
 *
 * Mirrors `mobile-app/src/components/hub/hub-header.tsx` lines 288–343 where
 * the "Your {gameName} id :" card sits below the banner — we collapse both
 * pieces into a single card so the Mini App's vertical budget is respected.
 */
export function GameHeader({ className }: GameHeaderProps) {
  const { activeGame } = useGameSelection();
  const me = useAppSelector((s) => s.auth.user);
  const userId = typeof me?.id === 'number' ? me.id : Number(me?.id);
  const hasUser = !!me && Number.isFinite(userId) && userId > 0;
  const { data: profiles } = useProfilesQuery(userId, { skip: !hasUser });
  const [open, setOpen] = useState(false);

  // For Chess, the game-specific id is the linked Lichess username (there's no
  // Laravel profile). Fetch it only when Chess is the active game.
  const isChess = activeGame?.id === CHESS_GAME_ID;
  const linkQuery = useGetLinkQuery(undefined, { skip: !isChess });
  const lichessUsername = linkQuery.data?.linked ? linkQuery.data.lichessUsername : null;

  // For Clash of Clans, the game-specific id is the linked CoC account name
  // (there's no Laravel profile). Fetch it only when CoC is the active game.
  const isCoc = activeGame?.id === COC_GAME_ID;
  const cocLinkQuery = useGetCocLinkQuery(undefined, { skip: !isCoc });
  const cocAccount = cocLinkQuery.data?.linked ? cocLinkQuery.data.accounts[0] : null;
  const cocName = cocAccount ? cocAccount.playerName || cocAccount.playerTag : null;

  // Clash Royale is a real Laravel game (id 6), but in the TMA the CR account is
  // linked via QBot — so the identity is QBot's `linkedCrTag` (from /me), which
  // already bridges the Laravel CR registration on cache miss (covers both
  // TMA-linked and native-app users). The generic Laravel-profile path below
  // misses TMA-only links → showed "— no profile yet —" for linked users.
  const isCr = featuresForGame(activeGame).cr;
  const crMe = useMeQuery(undefined, { skip: !isCr });
  const crTag = crMe.data?.linkedCrTag ?? null;
  // Prefer the in-game player name (like chess→username, coc→playerName); only
  // fetch the CR profile once we know a tag exists.
  const crProfile = useProfileMeQuery(undefined, { skip: !isCr || !crTag });
  const crName = crProfile.data?.player?.name ?? null;

  const activeProfile = useMemo<Profile | null>(() => {
    if (!activeGame) return null;
    const list = profiles?.data ?? [];
    // Backend serialises via `ShortProfile`, which omits the flat `game_id`
    // field and instead nests the game in `profile.game`. Older mobile-app
    // builds resolve via either path, so check both for safety.
    return (
      list.find((p) => {
        const flat = (p as unknown as { game_id?: number }).game_id;
        const nested = (p as unknown as { game?: { id?: number } }).game?.id;
        return flat === activeGame.id || nested === activeGame.id;
      }) ?? null
    );
  }, [profiles, activeGame]);

  const gamertag = formatGamertag(activeProfile);
  // CR identity, in preference order: in-game name → bare tag (strip the leading
  // '#' to match the rest of the CR UI) → Laravel gamertag fallback.
  const crIdentity = crName ?? (crTag ? crTag.replace(/^#/, '') : null) ?? gamertag;
  // Show the QLASH identity whenever we have any name field — username, else
  // first+last, else email. (Was username ?? firstName only, which hid the QLASH
  // line for accounts that have neither — e.g. only a last name or email set.)
  const accountName =
    me?.username ||
    [me?.firstName, me?.lastName].filter(Boolean).join(' ') ||
    me?.email ||
    null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={cn(
          'flex w-full items-center justify-start gap-3 rounded-2xl p-3 text-left transition-opacity active:opacity-80',
          activeGame
            ? 'bg-transparent'
            : 'border border-dashed border-border bg-transparent',
          className,
        )}
      >
        {activeGame ? (
          <>
            <span className="flex h-14 w-14 shrink-0 overflow-hidden rounded-xl ring-1 ring-brand">
              {activeGame.icon && (
                <img
                  src={activeGame.icon}
                  alt=""
                  className="h-full w-full object-cover"
                  loading="eager"
                />
              )}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-bold text-fg">{activeGame.name}</p>
              {accountName && (
                <p className="truncate text-xs text-fg-muted">
                  <span className="text-[10px] uppercase tracking-wide">QLASH</span>{' '}
                  <span className="font-semibold text-brand">{accountName}</span>
                </p>
              )}
              {isChess ? (
                lichessUsername && (
                  <p className="truncate text-xs text-fg-muted">
                    <span className="text-[10px] uppercase tracking-wide">Lichess</span>{' '}
                    <span className="font-semibold text-fg">{lichessUsername}</span>
                  </p>
                )
              ) : isCoc ? (
                cocName && (
                  <p className="truncate text-xs text-fg-muted">
                    <span className="text-[10px] uppercase tracking-wide">CoC</span>{' '}
                    <span className="font-semibold text-fg">{cocName}</span>
                  </p>
                )
              ) : isCr ? (
                crIdentity ? (
                  <p className="truncate text-xs text-fg-muted">
                    <span className="text-[10px] uppercase tracking-wide">CR</span>{' '}
                    <span className="font-semibold text-fg">{crIdentity}</span>
                  </p>
                ) : crMe.isLoading ? null : (
                  <p className="truncate text-xs font-semibold text-fg-muted">
                    — no profile yet —
                  </p>
                )
              ) : (
                <p className="truncate text-xs font-semibold text-fg-muted">
                  {gamertag ?? '— no profile yet —'}
                </p>
              )}
            </div>
            <ChevronDown aria-hidden className="h-5 w-5 shrink-0 text-fg-muted" />
          </>
        ) : (
          <>
            <span className="text-sm font-medium text-fg">Select your game</span>
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-bg-secondary text-fg-muted">
              <Plus aria-hidden className="h-5 w-5" />
            </span>
          </>
        )}
      </button>

      <GamePicker open={open} onClose={() => setOpen(false)} />
    </>
  );
}

interface GameInfoLike {
  id?: string;
  gameName?: string;
  tagLine?: string;
}

function formatGamertag(profile: Profile | null): string | null {
  if (!profile) return null;
  const info = profile.info as GameInfoLike | undefined;
  if (info?.gameName && info?.tagLine) return `${info.gameName}#${info.tagLine}`;
  if (info?.id && info.id !== 'unknown') return info.id;
  return null;
}
